import { GLB_SLIM } from "@greg-chick/shared";
import { slimGlb, type EncodedMime, type ImageEncoder, type SlimReport } from "./slimGlb";

/**
 * 🪶 **브라우저 캔버스로 그림을 다시 굽는 손** — `slimGlb` 에 넘긴다(2026-09-14).
 *
 * · 크기: 긴 변을 `maxPx` 로(비율 유지). 작으면 키우지 않는다.
 * · 형식: WebP(가장 작다). 캔버스가 WebP 를 못 만들면(사파리) 투명한 그림은 PNG, 아니면 JPEG.
 * · 🔴 색 변환을 끈다(`colorSpaceConversion: "none"`) — 법선 지도는 색이 아니라 **방향**이다. 색 보정이 들어가면 빛이 틀어진다.
 */
export const canvasEncode: ImageEncoder = async (data, mime, { maxPx, quality }) => {
  const bmp = await createImageBitmap(new Blob([data.slice()], { type: mime }), { colorSpaceConversion: "none", premultiplyAlpha: "none" });
  const k = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * k));
  const h = Math.max(1, Math.round(bmp.height * k));
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("이 브라우저는 그림을 다시 굽지 못해요(캔버스 없음)");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const px = ctx.getImageData(0, 0, w, h).data;
  let alpha = false;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i]! < 255) {
      alpha = true;
      break;
    }
  }
  const toBlob = (type: string) => new Promise<Blob | null>((res) => cv.toBlob(res, type, quality));
  let out: EncodedMime = "image/webp";
  let blob = await toBlob(out);
  if (!blob || blob.type !== "image/webp") {
    out = alpha ? "image/png" : "image/jpeg";
    blob = await toBlob(out);
  }
  if (!blob) throw new Error("그림을 다시 굽지 못했어요");
  return { data: new Uint8Array(await blob.arrayBuffer()), mime: out, px: Math.max(w, h) };
};

/** 고른 .glb 파일 → 줄인 .glb 파일(이름에 `_light`). 값(크기·품질)은 shared `GLB_SLIM` 한 곳. */
export async function slimGlbFile(file: File): Promise<{ file: File; report: SlimReport }> {
  const { glb, report } = await slimGlb(await file.arrayBuffer(), canvasEncode, GLB_SLIM);
  const name = `${file.name.replace(/\.glb$/i, "")}_light.glb`;
  return { file: new File([glb], name, { type: "model/gltf-binary" }), report };
}
